import React, { useState, useEffect, useRef } from 'react';

/**
 * Modal Avanzado para Nano Texto de Seguridad
 * Características de nivel profesional para documentos de alta seguridad
 */
const NanoTextModal = ({ isOpen, onClose, onGenerate }) => {
    // Estados principales
    const [text, setText] = useState('SECURITY • AUTHORIZED • ORIGINAL • VALID');
    const [nanoSize, setNanoSize] = useState('100'); // nanómetros
    const [density, setDensity] = useState('high');
    const [fontFamily, setFontFamily] = useState('Share Tech Mono');
    const [fontWeight, setFontWeight] = useState('700');
    const [pattern, setPattern] = useState('matrix');
    const [inkType, setInkType] = useState('silver');
    const [opacity, setOpacity] = useState(0.2);
    const [contrast, setContrast] = useState(1.0);
    const [repeatCount, setRepeatCount] = useState(500);

    // Estados avanzados
    const [enableEncryption, setEnableEncryption] = useState(false);
    const [encryptionKey, setEncryptionKey] = useState('');
    const [enableChecksum, setEnableChecksum] = useState(true);
    const [checksumType, setChecksumType] = useState('crc32');
    const [enableSteganography, setEnableSteganography] = useState(false);
    const [hiddenMessage, setHiddenMessage] = useState('');

    // Canvas para preview
    const canvasRef = useRef(null);

    useEffect(() => {
        if (isOpen && canvasRef.current) {
            updatePreview();
        }
    }, [isOpen, text, nanoSize, density, pattern, opacity, contrast, fontFamily, fontWeight]);

    const updatePreview = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const width = canvas.width;
        const height = canvas.height;

        // Limpiar canvas
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, width, height);

        // Simular nano estructuras
        const displayText = text || 'SECURITY';
        ctx.font = `${fontWeight} 2px "${fontFamily}"`;
        ctx.fillStyle = `rgba(0, 255, 100, ${opacity * contrast})`;

        // Generar patrón según tipo
        const positions = generateNanoPattern(pattern, 200, width, height);

        positions.forEach((pos, idx) => {
            const char = displayText[idx % displayText.length];
            ctx.fillText(char, pos.x, pos.y);
        });

        // Añadir ruido cuántico para simular efecto microscópico
        for (let i = 0; i < 1000; i++) {
            const x = Math.random() * width;
            const y = Math.random() * height;
            const r = Math.random() * 255;
            const g = Math.random() * 255;
            const b = Math.random() * 255;
            ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${opacity * 0.1})`;
            ctx.fillRect(x, y, 1, 1);
        }
    };

    const generateNanoPattern = (patternType, count, width, height) => {
        const positions = [];

        switch (patternType) {
            case 'matrix':
                const rows = Math.sqrt(count);
                const cols = Math.sqrt(count);
                for (let i = 0; i < rows; i++) {
                    for (let j = 0; j < cols; j++) {
                        positions.push({
                            x: (j / cols) * width,
                            y: (i / rows) * height
                        });
                    }
                }
                break;

            case 'fractal':
                for (let i = 0; i < count; i++) {
                    const t = i / count;
                    const ang = t * Math.PI * 8;
                    const r = t * 50;
                    positions.push({
                        x: width / 2 + r * Math.cos(ang) * Math.cos(ang * 3),
                        y: height / 2 + r * Math.sin(ang) * Math.sin(ang * 2)
                    });
                }
                break;

            case 'dna':
                for (let i = 0; i < count; i++) {
                    const t = (i / count) * Math.PI * 4;
                    const strand = i % 2;
                    const radius = strand === 0 ? 20 : 25;
                    positions.push({
                        x: (i / count) * width,
                        y: height / 2 + radius * Math.sin(t + strand * Math.PI)
                    });
                }
                break;

            case 'quantum':
            default:
                for (let i = 0; i < count; i++) {
                    positions.push({
                        x: Math.random() * width,
                        y: Math.random() * height
                    });
                }
                break;
        }

        return positions;
    };

    const calculateChecksum = (data, type) => {
        // Implementación simplificada de checksum
        switch (type) {
            case 'crc32':
                let crc = 0xFFFFFFFF;
                for (let i = 0; i < data.length; i++) {
                    crc ^= data.charCodeAt(i);
                    for (let j = 0; j < 8; j++) {
                        crc = (crc >>> 1) ^ (0xEDB88320 & -(crc & 1));
                    }
                }
                return (crc ^ 0xFFFFFFFF) >>> 0;

            case 'md5':
                // Simulación de MD5 (en producción usar crypto)
                let hash = 0;
                for (let i = 0; i < data.length; i++) {
                    hash = ((hash << 5) - hash) + data.charCodeAt(i);
                    hash = hash & hash;
                }
                return Math.abs(hash).toString(16);

            case 'sha256':
                // Simulación de SHA256 (en producción usar crypto)
                let sha = 0;
                for (let i = 0; i < data.length; i++) {
                    sha = ((sha << 7) - sha) + data.charCodeAt(i);
                    sha = sha & sha;
                }
                return Math.abs(sha).toString(16).padStart(64, '0');

            default:
                return data.length.toString(16);
        }
    };

    // Función auxiliar para codificar UTF-8 a Base64 de forma segura
    const safeBase64Encode = (str) => {
        try {
            // Convertir a UTF-8 bytes y luego a Base64
            const utf8Bytes = new TextEncoder().encode(str);
            const binaryString = Array.from(utf8Bytes, byte => String.fromCharCode(byte)).join('');
            return btoa(binaryString);
        } catch (e) {
            console.error('Error en codificación Base64:', e);
            // Fallback: intentar con escape
            try {
                return btoa(unescape(encodeURIComponent(str)));
            } catch (e2) {
                console.error('Error en fallback de codificación:', e2);
                return str; // Último recurso: devolver el string original
            }
        }
    };

    const encryptText = (plaintext, key) => {
        if (!key) return plaintext;

        // XOR encryption simple (en producción usar AES)
        let encrypted = '';
        for (let i = 0; i < plaintext.length; i++) {
            const charCode = plaintext.charCodeAt(i) ^ key.charCodeAt(i % key.length);
            encrypted += String.fromCharCode(charCode);
        }

        // Usar codificación segura
        return safeBase64Encode(encrypted);
    };

    const handleGenerate = () => {
        let finalText = text.trim();

        if (!finalText) {
            alert('Por favor ingresa el texto de seguridad nano');
            return;
        }

        // Aplicar encriptación si está habilitada
        if (enableEncryption && encryptionKey) {
            finalText = encryptText(finalText, encryptionKey);
        }

        // Añadir checksum si está habilitado
        if (enableChecksum) {
            const checksum = calculateChecksum(finalText, checksumType);
            finalText = `${finalText}[CHK:${checksum}]`;
        }

        // Añadir esteganografía si está habilitada
        if (enableSteganography && hiddenMessage) {
            const encoded = safeBase64Encode(hiddenMessage);
            finalText = `${finalText}[STEG:${encoded}]`;
        }

        // Configurar densidad
        const densityMap = {
            ultra: { chars: 900, fontSize: 0.6 },
            high: { chars: 600, fontSize: 0.85 },
            medium: { chars: 380, fontSize: 1.0 },
            low: { chars: 220, fontSize: 1.4 }
        };

        const config = densityMap[density];

        onGenerate({
            text: finalText,
            nanoSize: parseInt(nanoSize),
            density,
            charCount: config.chars,
            fontSize: config.fontSize,
            fontFamily,
            fontWeight,
            pattern,
            inkType,
            opacity: parseFloat(opacity),
            contrast: parseFloat(contrast),
            repeatCount: parseInt(repeatCount),
            customType: 'nanoText',
            isSecurityFeature: true,
            securityLevel: 'quantum',
            metadata: {
                encrypted: enableEncryption,
                checksumType: enableChecksum ? checksumType : null,
                hasSteganography: enableSteganography,
                timestamp: new Date().toISOString()
            }
        });

        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden border border-slate-700">
                {/* Header */}
                <div className="p-6 border-b border-slate-700 bg-gradient-to-r from-blue-900/50 to-purple-900/50">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center">
                                <span className="text-3xl">🔬</span>
                            </div>
                            <div>
                                <h3 className="text-2xl font-bold text-white">Nano Texto de Seguridad</h3>
                                <p className="text-sm text-blue-300">Tecnología de nivel gubernamental • Visible solo con microscopio</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-slate-400 hover:text-white transition-colors p-2 hover:bg-slate-700 rounded-lg"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 max-h-[70vh] overflow-y-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Columna Izquierda - Configuración Básica */}
                        <div className="space-y-4">
                            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                <h4 className="text-sm font-bold text-blue-400 mb-3 flex items-center">
                                    <span className="mr-2">📝</span> Configuración Básica
                                </h4>

                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-2">
                                            Texto de Seguridad Microscópico
                                        </label>
                                        <textarea
                                            value={text}
                                            onChange={(e) => setText(e.target.value)}
                                            rows={3}
                                            className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="SECURITY • AUTHORIZED • ORIGINAL"
                                        />
                                        <p className="text-xs text-slate-500 mt-1">⚠️ Visible solo con amplificación 500x+ o microscopio electrónico</p>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">Tamaño (nanómetros)</label>
                                            <select
                                                value={nanoSize}
                                                onChange={(e) => setNanoSize(e.target.value)}
                                                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500"
                                            >
                                                <option value="50">50nm - Ultra nano</option>
                                                <option value="100">100nm - Nano estándar</option>
                                                <option value="200">200nm - Nano visible</option>
                                                <option value="500">500nm - Límite óptico</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">Densidad</label>
                                            <select
                                                value={density}
                                                onChange={(e) => setDensity(e.target.value)}
                                                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500"
                                            >
                                                <option value="ultra">Ultra alta (10000 char/mm²)</option>
                                                <option value="high">Alta (5000 char/mm²)</option>
                                                <option value="medium">Media (2500 char/mm²)</option>
                                                <option value="low">Baja (1000 char/mm²)</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">Fuente Especializada</label>
                                            <select
                                                value={fontFamily}
                                                onChange={(e) => setFontFamily(e.target.value)}
                                                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500"
                                            >
                                                <option value="Share Tech Mono">Share Tech Mono (OCR)</option>
                                                <option value="Source Code Pro">Source Code Pro</option>
                                                <option value="Saira Extra Condensed">Saira Extra Condensed</option>
                                                <option value="Roboto Condensed">Roboto Condensed</option>
                                                <option value="IBM Plex Sans Condensed">IBM Plex Condensed</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">Peso de Fuente</label>
                                            <select
                                                value={fontWeight}
                                                onChange={(e) => setFontWeight(e.target.value)}
                                                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500"
                                            >
                                                <option value="400">Regular</option>
                                                <option value="600">SemiBold</option>
                                                <option value="700">Bold</option>
                                                <option value="900">Black</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-2">Patrón de Distribución</label>
                                        <div className="grid grid-cols-2 gap-2">
                                            {[
                                                { value: 'matrix', label: 'Matriz Criptográfica', icon: '⊞' },
                                                { value: 'fractal', label: 'Fractal de Koch', icon: '❋' },
                                                { value: 'dna', label: 'Helicoidal DNA', icon: '🧬' },
                                                { value: 'quantum', label: 'Cuántico Aleatorio', icon: '⚛' }
                                            ].map(p => (
                                                <label
                                                    key={p.value}
                                                    className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${pattern === p.value
                                                        ? 'border-blue-500 bg-blue-500/20 text-blue-300'
                                                        : 'border-slate-600 hover:border-slate-500 text-slate-400'
                                                        }`}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="pattern"
                                                        value={p.value}
                                                        checked={pattern === p.value}
                                                        onChange={(e) => setPattern(e.target.value)}
                                                        className="mr-2"
                                                    />
                                                    <div>
                                                        <div className="text-sm font-medium">{p.icon} {p.label}</div>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-3 gap-3">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">Tinta Especializada</label>
                                            <select
                                                value={inkType}
                                                onChange={(e) => setInkType(e.target.value)}
                                                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500"
                                            >
                                                <option value="carbon">Nanotubos de carbono</option>
                                                <option value="silver">Nanopartículas de plata</option>
                                                <option value="titanium">Óxido de titanio</option>
                                                <option value="quantum">Puntos cuánticos</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">Opacidad: {Math.round(opacity * 100)}%</label>
                                            <input
                                                type="range"
                                                min="0.05"
                                                max="0.8"
                                                step="0.05"
                                                value={opacity}
                                                onChange={(e) => setOpacity(parseFloat(e.target.value))}
                                                className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">Contraste: {Math.round(contrast * 100)}%</label>
                                            <input
                                                type="range"
                                                min="0.1"
                                                max="2.0"
                                                step="0.1"
                                                value={contrast}
                                                onChange={(e) => setContrast(parseFloat(e.target.value))}
                                                className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Preview Microscópico */}
                            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                <h4 className="text-sm font-bold text-green-400 mb-3 flex items-center">
                                    <span className="mr-2">🔍</span> Simulación Microscópica (ampliada 10,000x)
                                </h4>
                                <div className="w-full h-24 bg-black border border-slate-600 rounded-lg overflow-hidden">
                                    <canvas ref={canvasRef} width="400" height="96" className="w-full h-full" />
                                </div>
                                <p className="text-xs text-slate-500 mt-2">Vista previa simulada del patrón nano-estructural</p>
                            </div>
                        </div>

                        {/* Columna Derecha - Configuración Avanzada */}
                        <div className="space-y-4">
                            {/* Encriptación */}
                            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                <h4 className="text-sm font-bold text-purple-400 mb-3 flex items-center">
                                    <span className="mr-2">🔐</span> Encriptación Avanzada
                                </h4>

                                <div className="space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={enableEncryption}
                                            onChange={(e) => setEnableEncryption(e.target.checked)}
                                            className="w-4 h-4 text-purple-600 bg-slate-900 border-slate-600 rounded focus:ring-purple-500"
                                        />
                                        <span className="text-sm text-slate-300">Habilitar encriptación XOR</span>
                                    </label>

                                    {enableEncryption && (
                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">Clave de Encriptación</label>
                                            <input
                                                type="password"
                                                value={encryptionKey}
                                                onChange={(e) => setEncryptionKey(e.target.value)}
                                                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm font-mono focus:ring-2 focus:ring-purple-500"
                                                placeholder="Ingrese clave secreta..."
                                            />
                                            <p className="text-xs text-slate-500 mt-1">🔒 La clave se requiere para decodificar el texto</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Checksum */}
                            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                <h4 className="text-sm font-bold text-cyan-400 mb-3 flex items-center">
                                    <span className="mr-2">✓</span> Verificación de Integridad
                                </h4>

                                <div className="space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={enableChecksum}
                                            onChange={(e) => setEnableChecksum(e.target.checked)}
                                            className="w-4 h-4 text-cyan-600 bg-slate-900 border-slate-600 rounded focus:ring-cyan-500"
                                        />
                                        <span className="text-sm text-slate-300">Añadir checksum de verificación</span>
                                    </label>

                                    {enableChecksum && (
                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">Algoritmo de Checksum</label>
                                            <select
                                                value={checksumType}
                                                onChange={(e) => setChecksumType(e.target.value)}
                                                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-cyan-500"
                                            >
                                                <option value="crc32">CRC32 (Rápido)</option>
                                                <option value="md5">MD5 (Estándar)</option>
                                                <option value="sha256">SHA-256 (Seguro)</option>
                                            </select>
                                            <p className="text-xs text-slate-500 mt-1">✓ Detecta modificaciones no autorizadas</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Esteganografía */}
                            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                <h4 className="text-sm font-bold text-amber-400 mb-3 flex items-center">
                                    <span className="mr-2">👁️</span> Esteganografía (Mensaje Oculto)
                                </h4>

                                <div className="space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={enableSteganography}
                                            onChange={(e) => setEnableSteganography(e.target.checked)}
                                            className="w-4 h-4 text-amber-600 bg-slate-900 border-slate-600 rounded focus:ring-amber-500"
                                        />
                                        <span className="text-sm text-slate-300">Ocultar mensaje secreto</span>
                                    </label>

                                    {enableSteganography && (
                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">Mensaje Oculto</label>
                                            <textarea
                                                value={hiddenMessage}
                                                onChange={(e) => setHiddenMessage(e.target.value)}
                                                rows={2}
                                                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm font-mono focus:ring-2 focus:ring-amber-500"
                                                placeholder="Mensaje invisible..."
                                            />
                                            <p className="text-xs text-slate-500 mt-1">👁️ Codificado en Base64 dentro del patrón</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Información Técnica */}
                            <div className="bg-gradient-to-br from-blue-900/30 to-purple-900/30 rounded-lg p-4 border border-blue-700/50">
                                <h4 className="text-sm font-bold text-blue-300 mb-2">ℹ️ Información Técnica</h4>
                                <div className="space-y-1 text-xs text-slate-400">
                                    <p>• <strong>Resolución mínima:</strong> 4800 DPI (fotolitografía)</p>
                                    <p>• <strong>Tecnología:</strong> Impresión láser de precisión nanométrica</p>
                                    <p>• <strong>Verificación:</strong> Microscopio electrónico o lupa 500x+</p>
                                    <p>• <strong>Nivel de seguridad:</strong> Gubernamental / Documentos clase A</p>
                                    <p>• <strong>Resistencia:</strong> Permanente, imposible de copiar con escáneres</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-slate-700 bg-slate-900/50 flex justify-between items-center">
                    <div className="text-xs text-slate-500">
                        <p>🔬 Tecnología de Nano Impresión Profesional</p>
                        <p>Compatible con impresoras de alta resolución (4800+ DPI)</p>
                    </div>
                    <div className="flex space-x-3">
                        <button
                            onClick={onClose}
                            className="px-6 py-2.5 text-sm font-medium text-slate-300 bg-slate-800 border border-slate-600 rounded-lg hover:bg-slate-700 transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleGenerate}
                            className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg shadow-blue-500/50"
                        >
                            🔬 Crear Nano Texto de Seguridad
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NanoTextModal;
